import React, { useMemo } from 'react';
import { propertyTypeMappings } from 'helpers/property_types';
import ManagedInput from 'components/form/managed_input';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { Alert } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from 'lodash';

interface PropertyTypePickerProps {
  defaultPropertyTypes?: number[] | null;
  selectAllPropertyTypes?: boolean;
  selectedPropertyTypes?: number[] | null;
  onUpdatePropertyType: (payload: { propertyTypes?: number[], selectAllPropertyTypes?: boolean }) => void;
}

export default function PropertyTypePicker({
  defaultPropertyTypes,
  selectedPropertyTypes = [],
  selectAllPropertyTypes,
  onUpdatePropertyType
}: PropertyTypePickerProps) {
  const handleSetDefaults = () => {
    onUpdatePropertyType({ selectAllPropertyTypes: false, propertyTypes: defaultPropertyTypes || [] });
  };

  const handleUpdatePropertyType = (id: number, value: boolean) => {
    if (value) {
      onUpdatePropertyType({ propertyTypes: [...(selectedPropertyTypes || []), id] });
    } else {
      onUpdatePropertyType({ propertyTypes: [...(selectedPropertyTypes || []).filter(typeId => typeId !== id)] });
    }
  };

  const canSetDefault : boolean = useMemo(() => {
    return !!defaultPropertyTypes && defaultPropertyTypes.length > 0 &&
           !selectAllPropertyTypes && !isEqual(defaultPropertyTypes?.sort(), selectedPropertyTypes?.sort());
  }, [defaultPropertyTypes, selectAllPropertyTypes, selectedPropertyTypes]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The property types selected have been edited from the initial configuration.
          Selecting "revert" below will undo those changes,
          and make this document visible to property types selected when this template was created.
        </div>
        <div className="btn btn-link btn-sm" onClick={handleSetDefaults}>
          <FontAwesomeIcon className="me-2" icon={faUndo} />
          Revert Available Property Types
        </div>
      </Alert>
    );
  };

  return (
    <React.Fragment>
      <ToggleOverrideSection
        checked={selectAllPropertyTypes}
        name="property-type-override"
        subtitle={selectAllPropertyTypes ?
          'Unchecking will allow you to grant access to this document template on a per property type basis' :
          'Checking will grant access to this document template for all property types'
        }
        title="Enable for all property types"
        onOverride={handleSetDefaults}
        onRemove={() => onUpdatePropertyType({ selectAllPropertyTypes: true })}
      />
      {renderDefaultAlert()}
      {!selectAllPropertyTypes && Object.entries(propertyTypeMappings).map(([id, label]) => (
        <ManagedInput
          key={id}
          label={label}
          name={id}
          type="checkbox"
          value={(selectedPropertyTypes || []).includes(parseInt(id))}
          onValueChange={(value: boolean) => handleUpdatePropertyType(parseInt(id), value)}
        >
          {label}
        </ManagedInput>
      ))}
    </React.Fragment>
  );
}
